import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../lib/api';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';

export default function PublicProfile({ forcedUsername }) {
  const { username: paramUsername } = useParams();
  const username = forcedUsername || paramUsername;
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const ALL_SECTIONS = ['websites', 'contact', 'socials', 'music', 'videos', 'photos'];

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const { profile: data, activeDomain } = await api.get(`public/profile/${encodeURIComponent(username)}`);
        setProfile(data);

        // If this profile has an active custom domain
        // and we are NOT already on that domain, redirect to it.
        if (activeDomain && window.location.hostname !== activeDomain) {
          // Check if we are on a platform domain before redirecting to avoid loops
          const platformDomains = ['tapbadge.net', 'tapbadge.app', 'tapbadge.com', 'localhost'];
          const isPlatform = platformDomains.some(d => window.location.hostname.endsWith(d));

          if (isPlatform) {
            window.location.href = `https://${activeDomain}`;
          }
        }

      } catch (err) {
        setError(err.status === 404 ? "Profile not found" : "Could not load profile");
      } finally {
        setLoading(false);
      }
    };

    if (username) fetchProfile();
  }, [username]);

  // Handle Dynamic Browser Title
  useEffect(() => {
    if (profile) {
      const isPro = profile.subscription_status === 'active' || profile.is_lifetime;
      if (isPro && profile.meta_title) {
        document.title = profile.meta_title;
      } else {
        document.title = `${profile.first_name} ${profile.last_name} | TapBadge`;
      }
    }

    return () => {
      document.title = "TapBadge | The Ultimate NFC Digital Business Card";
    };
  }, [profile]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  if (error || !profile) return <div className="min-h-screen flex flex-col items-center justify-center p-4 text-center"><h1 className="text-2xl font-bold">{error}</h1>{!forcedUsername && <Link to="/" className="text-indigo-600 mt-4 underline">Back to Home</Link>}</div>;

  const isPro = profile.subscription_status === 'active' || profile.is_lifetime;
  const showFooter = !profile.hide_footer || !isPro;

  let sectionOrder = profile.section_order || ALL_SECTIONS;
  const resolvedOrder = [...sectionOrder];
  ALL_SECTIONS.forEach(s => {
    if (!resolvedOrder.includes(s)) resolvedOrder.push(s);
  });

  const downloadVCF = () => {
    const vcard = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      `FN:${profile.first_name} ${profile.last_name}`,
      `ORG:${profile.company || ''}`,
      `TITLE:${profile.title || ''}`,
      profile.contact_email ? `EMAIL;TYPE=INTERNET;TYPE=WORK:${profile.contact_email}` : '',
      profile.phone_mobile ? `TEL;TYPE=CELL:${profile.phone_mobile}` : '',
      profile.phone_business ? `TEL;TYPE=WORK:${profile.phone_business}` : '',
      `ADR;TYPE=WORK:;;${profile.address_line1 || ''};${profile.city || ''};${profile.state || ''};${profile.zip || ''};`,
      'END:VCARD'
    ].filter(l => l !== '').join('\n');

    const blob = new Blob([vcard], { type: 'text/vcard' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${profile.first_name}_contact.vcf`;
    link.click();
  };

  const renderSection = (type) => {
    switch (type) {
      case 'websites':
        return (profile.custom_websites || []).length > 0 && (
          <div className="space-y-3 mb-8">
            {profile.custom_websites.map((site, idx) => (
              <a key={idx} href={site.url} target="_blank" rel="noreferrer" className="flex items-center gap-4 bg-white border border-gray-100 rounded-2xl p-4 shadow-sm hover:border-indigo-600 transition-all group">
                <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition-colors"><SafeIcon name="Globe" size={20} /></div>
                <span className="font-bold text-gray-900 flex-1">{site.label || 'Website'}</span>
                <SafeIcon name="ExternalLink" size={14} className="text-gray-300" />
              </a>
            ))}
          </div>
        );
      case 'contact':
        const hasContact = profile.contact_email || profile.phone_mobile || profile.phone_business || profile.address_line1;
        return hasContact && (
          <div className="mb-8 p-6 bg-gray-50/80 backdrop-blur-sm rounded-[2rem] border border-gray-100 space-y-5">
            {profile.contact_email && (
              <div className="flex items-center gap-4">
                <a href={`mailto:${profile.contact_email}`} title="Send Email" className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-indigo-600 shadow-sm hover:bg-indigo-600 hover:text-white transition-all"><SafeIcon name="Mail" size={18} /></a>
                <div className="flex-1 min-w-0"><p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-none mb-1">Email</p><a href={`mailto:${profile.contact_email}`} className="text-sm font-bold text-gray-900 break-all hover:text-indigo-600">{profile.contact_email}</a></div>
              </div>
            )}
            {profile.phone_mobile && (
              <div className="flex items-center gap-4">
                <div className="flex gap-2">
                  <a href={`tel:${profile.phone_mobile}`} title="Call Mobile" className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-indigo-600 shadow-sm hover:bg-indigo-600 hover:text-white transition-all"><SafeIcon name="Smartphone" size={18} /></a>
                  <a href={`sms:${profile.phone_mobile}`} title="Text Mobile" className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-indigo-600 shadow-sm hover:bg-indigo-600 hover:text-white transition-all"><SafeIcon name="MessageSquare" size={18} /></a>
                </div>
                <div className="flex-1"><p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-none mb-1">Mobile</p><p className="text-sm font-bold text-gray-900">{profile.phone_mobile}</p></div>
              </div>
            )}
            {profile.phone_business && (
              <div className="flex items-center gap-4">
                <a href={`tel:${profile.phone_business}`} className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-indigo-600 shadow-sm hover:bg-indigo-600 hover:text-white transition-all"><SafeIcon name="Phone" size={18} /></a>
                <div className="flex-1"><p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-none mb-1">Business</p><p className="text-sm font-bold text-gray-900">{profile.phone_business}</p></div>
              </div>
            )}
            {profile.address_line1 && (
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-indigo-600 shadow-sm"><SafeIcon name="MapPin" size={18} /></div>
                <div className="flex-1">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-none mb-1">Location</p>
                  <p className="text-sm font-bold text-gray-900 leading-tight">
                    {profile.address_line1}<br />
                    {profile.city}, {profile.state} {profile.zip}
                  </p>
                </div>
              </div>
            )}
          </div>
        );
      case 'music':
        const musicLinks = Object.entries(profile.music_links || {}).filter(([_, v]) => v);
        return musicLinks.length > 0 && (
          <div className="mb-8 space-y-3">
            <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1 mb-2">Music Profiles</h3>
            {musicLinks.map(([platform, url]) => (
              <a key={platform} href={url} target="_blank" rel="noreferrer" className="flex items-center gap-4 bg-white border border-gray-100 rounded-2xl p-4 shadow-sm group hover:shadow-md transition-all">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-lg ${platform === 'spotify' ? 'bg-[#1DB954]' : platform.includes('apple') ? 'bg-gradient-to-r from-pink-500 to-red-500' : 'bg-gray-900'}`}>
                  <SafeIcon name={platform.includes('youtube') ? 'Youtube' : 'Music'} size={20} />
                </div>
                <div className="flex-1">
                  <p className="text-[10px] font-bold text-gray-400 uppercase leading-none mb-1">Listen on</p>
                  <p className="font-bold text-gray-900 capitalize">{platform.replace('_', ' ')}</p>
                </div>
                <SafeIcon name="Play" size={12} className="text-indigo-600" />
              </a>
            ))}
          </div>
        );
      case 'socials':
        const socials = Object.entries(profile.social || {}).filter(([_, v]) => v);
        return socials.length > 0 && (
          <div className="mb-8 grid grid-cols-4 gap-4">
            {socials.map(([p, h]) => (
              <a key={p} href={`https://${p}.com/${h}`} target="_blank" rel="noreferrer" className="aspect-square bg-white border border-gray-100 rounded-2xl flex items-center justify-center text-gray-400 hover:text-indigo-600 shadow-sm">
                <SafeIcon name={p.charAt(0).toUpperCase() + p.slice(1)} size={24} />
              </a>
            ))}
          </div>
        );
      case 'videos':
        return (profile.youtube_videos || []).length > 0 && (
          <div className="mb-10 space-y-4">
            {profile.youtube_videos.map((v, i) => {
              const yid = v.url.match(/^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/)?.[2];
              return yid && (
                <div key={i} className="rounded-2xl overflow-hidden shadow-lg aspect-video bg-gray-100">
                  <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${yid}`} frameBorder="0" allowFullScreen></iframe>
                </div>
              );
            })}
          </div>
        );
      case 'photos':
        return (profile.portfolio_photos || []).length > 0 && (
          <div className="mb-10 grid grid-cols-2 gap-4">
            {profile.portfolio_photos.map(p => (
              <div key={p.id} className="aspect-square rounded-2xl overflow-hidden shadow-md border border-gray-100">
                <img src={p.url} className="w-full h-full object-cover" alt="" />
              </div>
            ))}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen relative flex justify-center items-start sm:py-12 px-0 sm:px-4">
      {profile.page_background_url && (
        <div className="fixed inset-0 z-0 bg-cover bg-center" style={{ backgroundImage: `url(${profile.page_background_url})` }}>
          <div className="absolute inset-0 bg-black/30 backdrop-blur-[4px]" />
        </div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-white/95 backdrop-blur-xl sm:rounded-[3.5rem] shadow-2xl overflow-hidden relative z-10 min-h-screen sm:min-h-0 border border-white/20"
      >
        <div className="h-52 relative">
          {profile.banner_url ? <img src={profile.banner_url} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500" />}
        </div>

        <div className="px-6 pb-20 relative -mt-24">
          <div className="flex justify-center mb-6">
            <img src={profile.avatar_url} className="w-40 h-40 rounded-full border-[6px] border-white shadow-2xl object-cover bg-white" />
          </div>

          <div className="text-center mb-8">
            <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">{profile.first_name} {profile.last_name}</h1>
            <p className="text-lg text-indigo-600 font-bold mt-1">{profile.title}</p>
            <p className="text-gray-500 font-medium">{profile.company}</p>
          </div>

          <button onClick={downloadVCF} className="w-full bg-gray-900 text-white rounded-[2rem] py-5 font-bold text-lg mb-10 shadow-2xl flex items-center justify-center gap-3 hover:bg-black transition-all">
            <SafeIcon name="UserPlus" size={20} />
            Add to Contacts
          </button>

          <div className="space-y-2">
            {resolvedOrder.map(s => <React.Fragment key={s}>{renderSection(s)}</React.Fragment>)}
          </div>

          {showFooter && (
            <div className="mt-12 text-center pb-8">
              {/* Absolute URL: this page is also served on members' custom domains. */}
              <a
                href="https://tapbadge.app/"
                target="_blank"
                rel="noopener"
                className="text-[10px] font-bold uppercase tracking-widest opacity-40 hover:opacity-80 hover:text-indigo-600 transition-opacity"
              >
                Powered by TapBadge
              </a>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
